/*
 * @Description: 自动高亮图表
 * @Autor: zhuxingjie
 * @Date: 2022-07-21 10:10:40
 * @LastEditors: zhuxingjie
 * @LastEditTime: 2022-12-23 17:17:23
 */

/** 将图表id及其对应的定时器id和高亮索引维护在此Map实例中 */
const activeCharts = new Map();

export function useStartActions (ref, options={}) {

  // 无options时退出
  if (!ref.chart?.getOption()) return;
  const chartId = ref.chart.id;

  // 处理重复调用
  activeCharts.has(chartId) && handleDuplicateId(chartId, options);

  // 创建定时器方法并开启
  startActions(chartId, ref, options);

  // 处理图表事件
  handleMouseEvent(chartId, ref, options);
}

/**
 * @description: 处理同一个图表重复调用autoActions
 * @param chartId 图表实例id
 * @param options 配置项
 */
function handleDuplicateId(chartId, options) {
  const { actionTimer, dataIndex } = activeCharts.get(chartId);
  // 关闭上一个定时器
  clearInterval(actionTimer);
  // 判断是否保持上一次的索引
  options?.keepDataIndex
    ? // 只移除定时器id 保留原始index数据
      activeCharts.set(chartId, { dataIndex })
    : // 移除该图表的数据
      activeCharts.delete(chartId);
}

/**
 * @description: 开启定时器
 * @param {string} chartId 图表实例id
 * @param {ChartRef} ref vue-echarts实例
 * @param {Options} options 配置项
 * @return {NodeJS.Timer} 定时器id
 */
function startActions(
  chartId,
  ref,
  options
) {
  const action = () => {
    const { actionTimer } = activeCharts.get(chartId);
    // 图表实例被销毁时结束定时器
    if (!ref.chart) {
      activeCharts.delete(chartId);
      clearInterval(Number(actionTimer));
      return;
    }

    // 获取数据长度
    const dataLen = getDataLen(ref, options);

    // 无数据长度时结束定时器
    if (!dataLen) {
      activeCharts.delete(chartId);
      clearInterval(Number(actionTimer));
      return;
    }

    // 进行图表高亮
    dispatchAction(chartId, ref, dataLen);
  };
  const defaultInterval = 2000;
  const timer = setInterval(action, options?.interval ?? defaultInterval);
  // 更新activeCharts
  updateActiveCharts(chartId, timer);
  // 是否立即执行
  options?.immediate && action();
  return timer;
}

/**
 * @description:更新activeCharts
 * @param chartId 图表实例id
 * @param timer 定时器id
 */
function updateActiveCharts(chartId, timer) {
  if (activeCharts.has(chartId)) {
    // 设置定时器id
    const { dataIndex } = activeCharts.get(chartId);
    activeCharts.set(chartId, { actionTimer: timer, dataIndex });
  } else {
    // 设置定时器id且初始化dataIndex
    activeCharts.set(chartId, { actionTimer: timer, dataIndex: -1 });
  }
}

/**
 * @description: 鼠标移入移出事件
 * @param {string} chartId 图表实例id
 * @param {ChartRef} ref vue-echarts实例
 * @param {Options} options 配置项
 */
function handleMouseEvent(
  chartId,
  ref,
  options
) {
  // 鼠标移动时
  ref?.chart?.on("mouseover", () => {
    const { dataIndex, actionTimer } = activeCharts.get(chartId);
    ref.dispatchAction({
      type: "downplay",
      seriesIndex: 0,
      dataIndex,
    });
    // 清除activeCharts中的定时器id并关闭定时器
    if (actionTimer) {
      clearInterval(actionTimer);
      activeCharts.set(chartId, { dataIndex });
    }
  });

  // 鼠标移出时
  ref?.chart?.getZr().on("globalout", () => {
    const collection = activeCharts.get(chartId);
    if (!collection.actionTimer) {
      //  更新activeCharts中的定时器数据并重新开启定时器
      const timer = startActions(chartId, ref, options);
      activeCharts.set(chartId, {
        ...collection,
        actionTimer: timer,
      });
    }
  });
}

/**
 * @description: 获取数据长度
 * @param {ChartRef} ref  vue-echarts实例
 * @param {Options} options 配置项
 * @return {number} 数据长度
 */
function getDataLen(ref, options) {
  let dataLen;
  if (options?.dataLen) {
    dataLen = options.dataLen;
  } else if (Array.isArray(ref.option?.series)) {
    dataLen = ref.option?.series[0].data.length;
  } else {
    dataLen = 0;
  }
  return dataLen;
}

/**
 * @description: 高亮图表
 * @param chartId 图表实例id
 * @param ref vue-echarts实例
 * @param dataLen 数据长度
 */
function dispatchAction(chartId, ref, dataLen) {
  const { dataIndex, actionTimer } = activeCharts.get(chartId);
  ref.dispatchAction({
    type: "downplay",
    seriesIndex: 0,
    dataIndex,
  });
  // 增加索引
  const increasedIndex = (dataIndex + 1) % dataLen;
  activeCharts.set(chartId, {
    actionTimer,
    dataIndex: increasedIndex,
  });
  ref.dispatchAction({
    type: "highlight",
    seriesIndex: 0,
    dataIndex: increasedIndex,
  });

  ref.dispatchAction({
    type: "showTip",
    seriesIndex: 0,
    dataIndex: increasedIndex,
  });
}
